import { useCallback, useState } from "react";
import { getBlockSortableId, type VisibleBlock } from "../utils/reorder-visible-blocks";

const orderOf = (visibleBlocks: VisibleBlock[]) =>
  visibleBlocks.map(({ block, index }) => getBlockSortableId(block, index));

const sameOrder = (a: string[], b: string[]) => a.length === b.length && a.every((id, i) => id === b[i]);

interface PendingOrder {
  from: string[];
  to: string[];
}

export const usePendingBlockOrder = () => {
  const [pending, setPending] = useState<PendingOrder>();

  const applyPendingOrder = useCallback(
    (visibleBlocks: VisibleBlock[]): VisibleBlock[] => {
      if (!pending) return visibleBlocks;
      if (!sameOrder(orderOf(visibleBlocks), pending.from)) return visibleBlocks;

      const byId = new Map(visibleBlocks.map((visible) => [getBlockSortableId(visible.block, visible.index), visible]));
      const reordered = pending.to.reduce<VisibleBlock[]>((acc, id) => {
        const found = byId.get(id);
        if (found) acc.push(found);
        return acc;
      }, []);

      return reordered.length === visibleBlocks.length ? reordered : visibleBlocks;
    },
    [pending]
  );

  const setPendingOrder = useCallback((from: VisibleBlock[], toIds: string[]) => {
    setPending({ from: orderOf(from), to: toIds });
  }, []);

  return { applyPendingOrder, setPendingOrder };
};
